import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixMembersV1DefaultPrivacy from './members-v1-default-privacy-default-privacy.http.js';

/**
 * Default privacy defines the initial privacy status assigned to new members when they join a site.
 * This setting determines whether new members start with public or private profiles by default.
 */
export interface DefaultPrivacy {
  /**
   * Privacy ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Default privacy status for a new member. */
  defaultPrivacy?: PrivacyWithLiterals;
  /** Revision number, which increments by 1 each time the default privacy is updated. To prevent conflicting changes, the existing revision must be used when updating default privacy. */
  revision?: string | null;
}

export enum Privacy {
  /** Default privacy status is private. All members have private profiles with no option to make them public. */
  PRIVATE = 'PRIVATE',
  /** Default privacy status is public. All members can make their profile public. */
  PUBLIC = 'PUBLIC',
}

/** @enumType */
export type PrivacyWithLiterals = Privacy | 'PRIVATE' | 'PUBLIC';

export interface GetDefaultPrivacyStatusRequest {}

export interface GetDefaultPrivacyStatusResponse {
  /** Retrieved default privacy settings. */
  defaultPrivacy?: DefaultPrivacy;
}

export interface SetDefaultPrivacyStatusRequest {
  /** Default privacy settings to set. */
  defaultPrivacy: DefaultPrivacy;
}

export interface SetDefaultPrivacyStatusResponse {
  /** Default privacy settings. */
  defaultPrivacy?: DefaultPrivacy;
}

/**
 * Retrieves the default privacy status of a site.
 * @public
 * @permissionId MEMBERS.DEFAULT_PRIVACY_STATUS_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.members.defaultprivacy.DefaultPrivacyStatus.GetDefaultPrivacyStatus
 */
export async function getDefaultPrivacyStatus(): Promise<
  NonNullablePaths<
    GetDefaultPrivacyStatusResponse,
    `defaultPrivacy.defaultPrivacy`,
    1
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixMembersV1DefaultPrivacy.getDefaultPrivacyStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Sets the default privacy status of a site.
 * @param defaultPrivacy - Default privacy settings to set.
 * @public
 * @requiredField defaultPrivacy
 * @requiredField defaultPrivacy.revision
 * @permissionId MEMBERS.DEFAULT_PRIVACY_STATUS_WRITE
 * @applicableIdentity APP
 * @fqn com.wixpress.members.defaultprivacy.DefaultPrivacyStatus.SetDefaultPrivacyStatus
 */
export async function setDefaultPrivacyStatus(
  defaultPrivacy: NonNullablePaths<DefaultPrivacy, `revision`, 0>
): Promise<
  NonNullablePaths<
    SetDefaultPrivacyStatusResponse,
    `defaultPrivacy.defaultPrivacy`,
    1
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    defaultPrivacy: defaultPrivacy,
  });

  const reqOpts =
    ambassadorWixMembersV1DefaultPrivacy.setDefaultPrivacyStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { defaultPrivacy: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['defaultPrivacy']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
